import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Globe, Building2, Bot, Eye, Zap } from 'lucide-react';

/**
 * UrlToCompanyChart_4 — "accumulation" content variant of UrlToCompanyChart (blog07).
 * Same five steps, same titles and details, but drawn as a cumulative build-up:
 * each row shows what the company HAS after that step, and every artifact earned
 * earlier stays on the board. The bar widens as the company accumulates — from a
 * pasted URL to a company profile, proposed bots & processes, human sign-off, and
 * finally a running, autonomy-earning workforce. No counts exist in the source
 * data, so artifacts are shown by name, never by number.
 */

const ACCENT = '#0AB3BF';
const SLATE_700 = '#334155';
const SLATE_500 = '#64748B';
const SLATE_200 = '#E2E8F0';

const STEPS = [
  {
    title: 'Paste a URL',
    icon: <Globe className="w-4 h-4" />,
    detail: 'A company website or LinkedIn page is enough.',
    adds: 'Website / LinkedIn URL',
  },
  {
    title: 'Read your public presence',
    icon: <Building2 className="w-4 h-4" />,
    detail: 'Brand voice, value props, products, personas — turned into a company profile.',
    adds: 'Company profile',
  },
  {
    title: 'Propose departments & bots',
    icon: <Bot className="w-4 h-4" />,
    detail: 'Bot roles and the processes they would run, per function.',
    adds: 'Proposed bots & processes',
  },
  {
    title: 'You review',
    icon: <Eye className="w-4 h-4" />,
    detail: 'Approve, edit, or reject each proposal before anything deploys.',
    adds: 'Your sign-off',
  },
  {
    title: 'Bots earn autonomy',
    icon: <Zap className="w-4 h-4" />,
    detail: 'Each bot is promoted at its own pace as your approvals build confidence.',
    adds: 'Running workforce',
  },
];

/** Single shared label formatter — used for the row header and the aria label. */
const formatStepLabel = (index: number, title: string) => `${index + 1}. ${title}`;

export const UrlToCompanyChart_4 = ({ height = 340 }: { height?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <div
      ref={ref}
      className="my-8 rounded-xl overflow-hidden border bg-white p-4 sm:p-6"
      style={{ minHeight: height, borderColor: SLATE_200 }}
    >
      <div className="space-y-2">
        {STEPS.map((s, i) => {
          const artifacts = STEPS.slice(0, i + 1).map((p) => p.adds);
          return (
            <motion.div
              key={s.title}
              initial={{ width: '20%', opacity: 0 }}
              animate={inView ? { width: `${34 + i * 16.5}%`, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.18, ease: 'easeOut' }}
              className="rounded-lg px-3 py-2 min-w-full sm:min-w-0"
              style={{ backgroundColor: `rgba(10, 179, 191, ${0.07 + i * 0.05})` }}
              aria-label={`${formatStepLabel(i, s.title)} — company now has: ${artifacts.join(', ')}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 bg-white border"
                  style={{ borderColor: SLATE_200, color: ACCENT }}
                >
                  {s.icon}
                </span>
                <span className="text-label font-semibold tracking-tight" style={{ color: SLATE_700 }}>
                  {formatStepLabel(i, s.title)}
                </span>
              </div>
              <p className="text-label-xs leading-snug mt-1" style={{ color: SLATE_500 }}>
                {s.detail}
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {artifacts.map((a, j) => {
                  const isNew = j === artifacts.length - 1;
                  return (
                    <span
                      key={a}
                      className="text-label-2xs font-medium px-1.5 py-0.5 rounded-full"
                      title={isNew ? `Added at step ${i + 1}` : `Kept from step ${j + 1}`}
                      style={
                        isNew
                          ? { backgroundColor: ACCENT, color: '#FFFFFF' }
                          : { backgroundColor: '#FFFFFF', color: SLATE_500, border: `1px solid ${SLATE_200}` }
                      }
                    >
                      {isNew ? `+ ${a}` : a}
                    </span>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>
      <p className="mt-4 text-label-xs leading-relaxed text-center" style={{ color: SLATE_500 }}>
        Figure 1. Watch the company accumulate: every step adds an artifact the company keeps — a profile, then
        proposed bots &amp; processes, then your sign-off — until a reviewed workforce is running and earning
        autonomy. Nothing deploys before the review step adds its approval.
      </p>
    </div>
  );
};
